import React, { useEffect, useRef, useState, useMemo } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';
import './MapField.css';

// Fix for default markers in Leaflet with webpack
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import markerRetina from 'leaflet/dist/images/marker-icon-2x.png';

const DefaultIcon = L.icon({
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
  iconRetinaUrl: markerRetina,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

// Generate static map image (no API calls for viewing)
const generateStaticMapImage = async (coords, mapRef) => {
  return new Promise((resolve) => {
    // First, try to capture the actual Leaflet map if it exists
    if (mapRef.current) {
      try {
        console.log('[MapField] 🌟 ATTEMPTING to capture actual Leaflet map image');
        console.log('[MapField] Map instance available:', !!mapRef.current);

        if (mapRef.current) {
          console.log('[MapField] Map container exists:', !!mapRef.current.getContainer());
          const container = mapRef.current.getContainer();
          console.log('[MapField] Container dimensions:', container.offsetWidth, 'x', container.offsetHeight);
        }

        // Use html2canvas to capture the actual map
        import('html2canvas').then((html2canvas) => {
          console.log('[MapField] html2canvas imported successfully');
          const mapContainer = mapRef.current.getContainer();

          // Wait for the map to be fully rendered and tiles loaded
          setTimeout(() => {
            console.log('[MapField] Executing html2canvas capture...');
            html2canvas.default(mapContainer, {
              useCORS: true,
              allowTaint: true,
              width: 600,
              height: 400,
              scale: 1,
              backgroundColor: '#ffffff',
              logging: false,
              foreignObjectRendering: false,
              removeContainer: false
            }).then((canvas) => {
              const dataUrl = canvas.toDataURL('image/png');
              console.log('[MapField] ✅ SUCCESS: Captured actual map image, size:', dataUrl.length, 'characters');
              console.log('[MapField] Image preview:', dataUrl.substring(0, 100) + '...');
              resolve(dataUrl);
            }).catch((error) => {
              console.error('[MapField] ❌ html2canvas capture failed:', error.message);
              generateFallbackMap(coords, resolve);
            });
          }, 3000); // Wait 3 seconds for map to render
        }).catch((error) => {
          console.error('[MapField] ❌ html2canvas import failed:', error);
          generateFallbackMap(coords, resolve);
        });
      } catch (error) {
        console.error('[MapField] ❌ Map capture setup failed:', error);
        generateFallbackMap(coords, resolve);
      }
    } else {
      console.log('[MapField] ❌ No map instance available, using fallback');
      generateFallbackMap(coords, resolve);
    }
  });
};

const generateFallbackMap = (coords, resolve) => {
  const canvas = document.createElement('canvas');
  canvas.width = 600;
  canvas.height = 400;
  const ctx = canvas.getContext('2d');

  // Create a more realistic map-like appearance
  const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
  gradient.addColorStop(0, '#e8f4f8');
  gradient.addColorStop(0.5, '#d1ecf1');
  gradient.addColorStop(1, '#bee5eb');

  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Add major roads (horizontal and vertical)
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 6;
  ctx.lineCap = 'round';

  // Horizontal roads
  ctx.beginPath();
  ctx.moveTo(0, canvas.height * 0.3);
  ctx.lineTo(canvas.width, canvas.height * 0.3);
  ctx.moveTo(0, canvas.height * 0.7);
  ctx.lineTo(canvas.width, canvas.height * 0.7);
  ctx.stroke();

  // Vertical roads
  ctx.beginPath();
  ctx.moveTo(canvas.width * 0.25, 0);
  ctx.lineTo(canvas.width * 0.25, canvas.height);
  ctx.moveTo(canvas.width * 0.75, 0);
  ctx.lineTo(canvas.width * 0.75, canvas.height);
  ctx.stroke();

  // Add minor roads
  ctx.strokeStyle = '#f8f9fa';
  ctx.lineWidth = 3;

  // Diagonal connecting roads
  ctx.beginPath();
  ctx.moveTo(canvas.width * 0.25, canvas.height * 0.3);
  ctx.lineTo(canvas.width * 0.75, canvas.height * 0.7);
  ctx.moveTo(canvas.width * 0.75, canvas.height * 0.3);
  ctx.lineTo(canvas.width * 0.25, canvas.height * 0.7);
  ctx.stroke();

  // Add building blocks (rectangles of different sizes)
  ctx.fillStyle = '#adb5bd';
  const buildingSizes = [
    [50, 80], [30, 60], [40, 50], [60, 40], [25, 45], [35, 55], [45, 35], [55, 65]
  ];

  buildingSizes.forEach((size, index) => {
    const x = (index % 4) * (canvas.width / 4) + Math.random() * 30;
    const y = Math.floor(index / 4) * (canvas.height / 2) + Math.random() * 50;
    ctx.fillRect(x, y, size[0], size[1]);

    // Add windows to some buildings
    if (Math.random() > 0.5) {
      ctx.fillStyle = '#495057';
      for (let wx = x + 5; wx < x + size[0] - 5; wx += 8) {
        for (let wy = y + 5; wy < y + size[1] - 5; wy += 8) {
          if (Math.random() > 0.3) {
            ctx.fillRect(wx, wy, 3, 3);
          }
        }
      }
      ctx.fillStyle = '#adb5bd';
    }
  });

  // Add trees/parks (green circles)
  ctx.fillStyle = '#28a745';
  for (let i = 0; i < 6; i++) {
    const x = Math.random() * canvas.width;
    const y = Math.random() * canvas.height;
    ctx.beginPath();
    ctx.arc(x, y, 8 + Math.random() * 6, 0, 2 * Math.PI);
    ctx.fill();
  }

  // Add a prominent red location marker (pin)
  const centerX = canvas.width / 2;
  const centerY = canvas.height / 2;

  // Pin shadow
  ctx.fillStyle = 'rgba(0,0,0,0.3)';
  ctx.beginPath();
  ctx.ellipse(centerX + 2, centerY + 2, 6, 4, 0, 0, 2 * Math.PI);
  ctx.fill();

  // Pin base
  ctx.fillStyle = '#dc3545';
  ctx.beginPath();
  ctx.arc(centerX, centerY, 8, 0, 2 * Math.PI);
  ctx.fill();

  // Pin point
  ctx.fillStyle = '#dc3545';
  ctx.beginPath();
  ctx.moveTo(centerX, centerY - 8);
  ctx.lineTo(centerX - 3, centerY - 12);
  ctx.lineTo(centerX + 3, centerY - 12);
  ctx.closePath();
  ctx.fill();

  // White dot in center of pin
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.arc(centerX, centerY, 3, 0, 2 * Math.PI);
  ctx.fill();

  const dataUrl = canvas.toDataURL('image/png');
  console.log('[MapField] ⚠️ Generated fallback map image (not real map)', dataUrl.length, 'characters');
  resolve(dataUrl);
};

const MapField = ({ field, value, onChange, readOnly, errors, allFormValues, formInstanceId }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [address, setAddress] = useState('');
  const [coordinates, setCoordinates] = useState(null);
  const [district, setDistrict] = useState(null);
  const [staticImage, setStaticImage] = useState(null);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);

  // Extract address components from form data
  const getAddressFromFormData = (formData) => {
    if (!formData) return '';

    // Try multiple field name patterns for compatibility
    // Pattern 1: Prefixed fields (used during form creation)
    const prefixedStreet = formInstanceId ? formData[`${formInstanceId}_home_street_address`] : null;
    const prefixedCity = formInstanceId ? formData[`${formInstanceId}_home_city`] : null;
    const prefixedState = formInstanceId ? formData[`${formInstanceId}_home_state`] : null;
    const prefixedZip = formInstanceId ? formData[`${formInstanceId}_home_zip_code`] : null;

    // Pattern 2: Non-prefixed fields (used in submitted/edited forms)
    const directStreet = formData.home_street_address || formData.street_address || '';
    const directCity = formData.home_city || formData.city || '';
    const directState = formData.home_state || formData.state || '';
    const directZip = formData.home_zip_code || formData.zip_code || formData.zip || '';

    // Use prefixed if available (form creation), otherwise use direct (form editing)
    const street = prefixedStreet || directStreet;
    const city = prefixedCity || directCity;
    const state = prefixedState || directState;
    const zip = prefixedZip || directZip;

    const parts = [street, city, state, zip].filter(part => part.trim());
    return parts.join(', ');
  };

  // Geocode address using backend proxy (avoids CORS issues)
  const geocodeAddress = async (addressString) => {
    if (!addressString.trim()) return null;

    setIsLoading(true);
    try {
      const encodedAddress = encodeURIComponent(addressString);
      const requestUrl = `/api/form-submissions/geocode?q=${encodedAddress}`;

      console.log(`[GEOCODE] Requesting: ${requestUrl}`);
      console.log(`[GEOCODE] Address: "${addressString}"`);

      const response = await axios.get(requestUrl);

      console.log(`[GEOCODE] Response status: ${response.status}`);
      console.log(`[GEOCODE] Response data:`, response.data);

      const data = response.data;

      if (data && data.length > 0) {
        const result = data[0];
        console.log(`[GEOCODE] Success: Found location for "${addressString}" -> ${result.lat}, ${result.lon} (${result.display_name})`);
        return {
          lat: parseFloat(result.lat),
          lng: parseFloat(result.lon),
          displayName: result.display_name
        };
      }

      console.log(`[GEOCODE] No results found for "${addressString}"`);
      return null;
    } catch (error) {
      console.error(`[GEOCODE] Error geocoding "${addressString}":`, {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url,
        method: error.config?.method
      });
      throw error; // Re-throw to allow caller to handle
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    // Initialize with Missouri center as fallback - will update when district loads
    const map = L.map(mapRef.current, {
      scrollWheelZoom: false, // Disable zoom on scroll to prevent accidental zooming
      doubleClickZoom: false, // Disable double-click zoom to prevent navigation
      boxZoom: false, // Disable box zoom
      keyboard: false // Disable keyboard navigation
    }).setView([38.5, -92.5], 7); // Start with Missouri center

    // Add OpenStreetMap tiles
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 18,
    }).addTo(map);

    mapInstanceRef.current = map;


    // Handle map clicks to place/update marker
    map.on('click', (e) => {
      console.log('Map clicked:', e.latlng);
      if (readOnly) {
        console.log('Map is read-only, ignoring click');
        return;
      }

      // Prevent event bubbling that might cause page refresh
      e.originalEvent?.preventDefault?.();
      e.originalEvent?.stopPropagation?.();

      const { lat, lng } = e.latlng;
      console.log('Placing marker at:', lat, lng);
      updateMarker(lat, lng);
      updateCoordinates(lat, lng); // Now async, but we don't need to await in event handler
    });

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [readOnly]);

  // Fetch district information for map center
  const fetchDistrictData = async () => {
    try {
      const response = await axios.get('/api/districts/current');
      setDistrict(response.data);
    } catch (error) {
      // If API fails, set fallback district data with Missouri center
      setDistrict({
        id: null,
        district_name: 'Default District',
        map_center: { lat: 37.9643, lng: -91.8318 },
        district_boundary: null
      });
    }
  };

  // Load district data on component mount
  useEffect(() => {
    fetchDistrictData();
  }, []);

  // Update map center when district data loads
  useEffect(() => {
    if (mapInstanceRef.current && district?.map_center) {
      mapInstanceRef.current.setView([district.map_center.lat, district.map_center.lng], 10);
    }
  }, [district]);

  // Handle map resize when container becomes visible
  useEffect(() => {
    if (mapInstanceRef.current) {
      // Invalidate size to fix display issues
      setTimeout(() => {
        if (mapInstanceRef.current) {
          mapInstanceRef.current.invalidateSize();
        }
      }, 100);
    }
  }, []);

  // Update marker position
  const updateMarker = (lat, lng) => {
    if (!mapInstanceRef.current) return;

    // Remove existing marker
    if (markerRef.current) {
      mapInstanceRef.current.removeLayer(markerRef.current);
    }

    // Create new marker
    const marker = L.marker([lat, lng], { draggable: !readOnly }).addTo(mapInstanceRef.current);

    if (!readOnly) {
      marker.on('dragend', (e) => {
        const { lat, lng } = e.target.getLatLng();
        updateCoordinates(lat, lng);
      });
    }

    markerRef.current = marker;
    mapInstanceRef.current.setView([lat, lng], 15);
  };

  // Update coordinates in form data
  const updateCoordinates = async (lat, lng) => {
    const coords = { lat, lng };
    setCoordinates(coords);
    console.log('[MapField] 🎯 MARKER MOVED - updating coordinates:', coords);

    // Generate static map image for caching - wait for map to be ready
    if (mapInstanceRef.current) {
      console.log('[MapField] 🖼️ Generating NEW cached map image after marker movement...');
      setIsGeneratingImage(true);
      try {
        // Wait for the map tiles to load
        setTimeout(async () => {
          const imageData = await generateStaticMapImage(coords, mapInstanceRef);

          // Store both coordinates and static image
          const valueWithImage = {
            ...coords,
            staticMapImage: imageData,
            imageGeneratedAt: new Date().toISOString(),
            imageVersion: 'v1'
          };

          console.log('[MapField] ✅ Generated and cached NEW static map image for coordinates:', coords);
          if (onChange) {
            onChange(valueWithImage);
          }
          setIsGeneratingImage(false);
        }, 3000); // Wait 3 seconds for map tiles to load
      } catch (error) {
        console.error('[MapField] ❌ Failed to generate static map image:', error);
        // Still store coordinates even if image generation fails
        if (onChange) {
          onChange(coords);
        }
        setIsGeneratingImage(false);
      }
    } else {
      console.log('[MapField] ⚠️ Map not ready yet, storing coordinates without generating image');
      // Store coordinates without image if map isn't ready
      if (onChange) {
        onChange(coords);
      }
    }

    // Also update the residence_coordinates field in the form data
    // This ensures the coordinates are stored in the expected location for residency forms
    if (allFormValues && typeof allFormValues === 'object') {
      // Check if this is a residency determination form (has residence_coordinates field)
      if (allFormValues.hasOwnProperty('residence_coordinates') || Object.keys(allFormValues).some(key => key.includes('residence'))) {
        console.log('[MapField] Updating residence_coordinates in form data');
        // The FormRenderer will handle updating allFormValues when onChange is called
        // We don't need to do anything special here as the coordinate storage is handled by the form system
      }
    }
  };

  // Calculate current address from form data
  const currentAddress = useMemo(() => {
    if (!allFormValues) return '';
    return getAddressFromFormData(allFormValues);
  }, [allFormValues, formInstanceId]);

  // Update address state when currentAddress changes
  useEffect(() => {
    if (currentAddress !== address) {
      setAddress(currentAddress);
    }
  }, [currentAddress, address]);

  // Handle coordinates display and static image generation
  useEffect(() => {
    if (value && typeof value === 'object') {
      // Check if we have coordinates in the value (direct map field value)
      if (value.lat && value.lng) {
        setCoordinates(value);

        // In read-only mode, use static image if available
        if (readOnly) {
          if (value.staticMapImage) {
            setStaticImage(value.staticMapImage);
          } else {
            // Generate static image for read-only mode if not cached
            setIsGeneratingImage(true);
            generateStaticMapImage(value, mapInstanceRef).then(imageData => {
              setStaticImage(imageData);
              setIsGeneratingImage(false);
            });
          }
        } else if (mapInstanceRef.current) {
          // In edit mode, update the interactive map
          updateMarker(value.lat, value.lng);
        }
      }

      // If we have a static image but no coordinates, still show it
      if (readOnly && value.staticMapImage && !staticImage) {
        setStaticImage(value.staticMapImage);
      }
    }

    // Also check for coordinates in form data (for existing submissions)
    // This handles cases where coordinates are stored separately from the map field
    if (allFormValues && !coordinates) {
      const residenceCoords = allFormValues.residence_coordinates;
      const directCoords = allFormValues.coordinates;

      let foundCoords = null;

      if (residenceCoords && typeof residenceCoords === 'object' && residenceCoords.lat && residenceCoords.lng) {
        foundCoords = residenceCoords;
      } else if (directCoords && typeof directCoords === 'object' && directCoords.lat && directCoords.lng) {
        foundCoords = directCoords;
      }

      if (foundCoords) {
        console.log('[MapField] 📍 LOADING existing coordinates from database:', foundCoords);
        setCoordinates(foundCoords);

        if (readOnly) {
          if (foundCoords.staticMapImage) {
            console.log('[MapField] 🖼️ Using EXISTING cached map image from database');
            setStaticImage(foundCoords.staticMapImage);
          } else {
            console.log('[MapField] ⚠️ No cached image in database, generating one for read-only view');
            // Generate static image for read-only mode if not cached
            setIsGeneratingImage(true);
            generateStaticMapImage(foundCoords, mapInstanceRef).then(imageData => {
              setStaticImage(imageData);
              setIsGeneratingImage(false);
            });
          }
        } else if (mapInstanceRef.current) {
          // In edit mode, update the interactive map
          updateMarker(foundCoords.lat, foundCoords.lng);

          // Always regenerate cached image when map becomes available
          console.log('[MapField] 🗺️ Map loaded, refreshing cached image for existing coordinates');
          setTimeout(() => {
            console.log('[MapField] 🔄 Regenerating cached image for existing coordinates');
            generateStaticMapImage(foundCoords, mapInstanceRef).then(imageData => {
              // Update the form data with the cached image
              const updatedCoords = {
                ...foundCoords,
                staticMapImage: imageData,
                imageGeneratedAt: new Date().toISOString(),
                imageVersion: 'v1'
              };

              console.log('[MapField] Updated cached image for existing coordinates');
              // Update the residence_coordinates field in form data
              if (onChange) {
                onChange(updatedCoords);
              }
            }).catch(error => {
              console.error('[MapField] Failed to generate cached image:', error);
            });
          }, 3000); // Wait 3 seconds for map to fully load
        }
      }
    }
  }, [value, readOnly, allFormValues, coordinates]);

  // Automatic geocoding when address changes
  const handleAutoGeocode = async (addressString) => {
    if (!addressString.trim() || isLoading) return;

    setIsLoading(true);
    try {
      const result = await geocodeAddress(addressString);
      if (result && mapInstanceRef.current) {
        updateMarker(result.lat, result.lng);
        await updateCoordinates(result.lat, result.lng);
      }
    } catch (error) {
      console.warn('Auto-geocoding failed:', error);
      // Don't show alert for auto-geocoding failures
    } finally {
      setIsLoading(false);
    }
  };

  // Manual geocode button handler
  const handleGeocode = async () => {
    if (!address.trim()) return;

    setIsLoading(true);
    try {
      const result = await geocodeAddress(address);

      if (result) {
        updateMarker(result.lat, result.lng);
        await updateCoordinates(result.lat, result.lng);
      } else {
        alert('Address not found. Please check the spelling and try again, or manually place the marker by clicking on the map.');
      }
    } catch (error) {
      console.error('[GEOCODE] Manual geocoding failed:', error);

      // Provide specific error messages based on backend response
      let errorMessage = 'Geocoding service is temporarily unavailable. Please try again in a moment.';

      if (error.response?.status) {
        const status = error.response.status;
        const backendError = error.response.data?.error;

        console.error(`[GEOCODE] Backend returned ${status}: ${backendError}`);

        switch (status) {
          case 400:
            errorMessage = 'Invalid address format. Please check the address and try again.';
            break;
          case 401:
            errorMessage = 'Authentication error. Please refresh the page and try again.';
            break;
          case 403:
            errorMessage = 'Geocoding service access denied. This may be due to rate limiting or service restrictions. Please try again later.';
            break;
          case 429:
            errorMessage = 'Too many geocoding requests. Please wait a moment before trying again.';
            break;
          case 408:
          case 504:
            errorMessage = 'Geocoding request timed out. Please try again.';
            break;
          case 500:
            errorMessage = 'Geocoding service error. Please try again later or contact support.';
            break;
          case 503:
            errorMessage = 'Geocoding service is temporarily unavailable. Please try again later.';
            break;
          default:
            errorMessage = `Geocoding failed (Error ${status}). Please try again or contact support.`;
        }

        if (backendError) {
          console.error(`[GEOCODE] Backend error details: ${backendError}`);
        }
      } else if (error.code === 'NETWORK_ERROR' || error.code === 'ERR_NETWORK') {
        errorMessage = 'Network error. Please check your connection and try again.';
      }

      alert(errorMessage + ' You can also manually place the marker by clicking on the map.');
    } finally {
      setIsLoading(false);
    }
  };

  const hasError = errors && errors[field.name];

  return (
    <div className="map-field-container">
      <div className="map-field-header">
        <div className="map-address-display">
          <strong>Current Address:</strong> {address || 'No address entered yet'}
        </div>
        {!readOnly && (
          <button
            type="button"
            className="btn-geocode"
            onClick={handleGeocode}
            disabled={isLoading || !address.trim()}
          >
            {isLoading ? 'Finding Location...' : 'Find on Map'}
          </button>
        )}
        {readOnly && coordinates && (
          <div className="map-status">
            <small>✓ Location verified</small>
          </div>
        )}
      </div>

      {readOnly && staticImage ? (
        // Display static map image in read-only mode
        <div className="static-map-container">
          {isGeneratingImage ? (
            <div className="map-loading" style={{
              height: '200px',
              width: '400px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#f8f9fa',
              border: '1px solid #dee2e6',
              borderRadius: '4px'
            }}>
              Generating map image...
            </div>
          ) : (
            <img
              src={staticImage}
              alt="Location Map"
              className="static-map-image"
              style={{
                width: '400px',
                height: '200px',
                border: '1px solid #dee2e6',
                borderRadius: '4px'
              }}
            />
          )}
        </div>
      ) : (
        // Interactive Leaflet map for editing
        <div className="map-container">
          <div
            ref={mapRef}
            className="map-element"
            style={{ height: '400px', width: '100%' }}
            onClick={(e) => {
              // Prevent any click events from bubbling up that might cause form submission or page refresh
              console.log('Map container clicked - preventing bubble');
              e.stopPropagation();
              e.preventDefault();
            }}
          />
        </div>
      )}

      {coordinates && (
        <div className="map-coordinates">
          <small>
            Coordinates: {coordinates.lat.toFixed(6)}, {coordinates.lng.toFixed(6)}
          </small>
        </div>
      )}

      {field.note && (
        <div className="field-note">
          {field.note}
        </div>
      )}

      {hasError && (
        <span className="error-message">{hasError}</span>
      )}
    </div>
  );
};

export default MapField;
